/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * The 12- or 14-byte record every FIT file opens with.
 *
 * [dataSize] counts only the records between this header and the file CRC, so an
 * encoder cannot fill it in until it has finished writing. The 14-byte form adds
 * a CRC over the first 12 bytes; the 12-byte form omits it.
 */
public class FileHeader(
    public val headerSize: Int = Fit.HEADER_WITH_CRC_SIZE,
    /**
     * The protocol version byte exactly as it appears in the file.
     *
     * Kept raw rather than as a [ProtocolVersion]: the byte packs a major
     * version in its high nibble and a minor in its low one, so producers write
     * values the enum does not name. Narrowing it here would silently rewrite
     * such a file's header.
     */
    public val protocolVersionByte: UByte = ProtocolVersion.V2_0.value,
    public val profileVersion: UShort = Fit.PROFILE_VERSION,
    public var dataSize: UInt = 0u,
    public val dataType: String = FIT_DATA_TYPE,
) {
    public constructor(
        headerSize: Int = Fit.HEADER_WITH_CRC_SIZE,
        protocolVersion: ProtocolVersion,
        profileVersion: UShort = Fit.PROFILE_VERSION,
        dataSize: UInt = 0u,
        dataType: String = FIT_DATA_TYPE,
    ) : this(headerSize, protocolVersion.value, profileVersion, dataSize, dataType)

    /** The named protocol version, or [ProtocolVersion.INVALID] if unrecognised. */
    public val protocolVersion: ProtocolVersion get() = ProtocolVersion.fromValue(protocolVersionByte)

    public val protocolVersionMajor: Int get() = (protocolVersionByte.toInt() shr 4) and 0x0F

    public val protocolVersionMinor: Int get() = protocolVersionByte.toInt() and 0x0F
    public var headerCrc: UShort = 0u
        private set

    public val hasCrc: Boolean get() = headerSize == Fit.HEADER_WITH_CRC_SIZE

    /** True when this looks like a FIT header at all: right size, right magic. */
    public val isValid: Boolean
        get() = (headerSize == Fit.HEADER_WITH_CRC_SIZE || headerSize == Fit.HEADER_WITHOUT_CRC_SIZE) &&
            dataType == FIT_DATA_TYPE

    /** Recomputes [headerCrc] over the first 12 bytes. */
    public fun updateCrc() {
        headerCrc = Crc.calculate(toByteArray(), 0, 12)
    }

    public fun write(writer: ByteWriter) {
        writer.writeBytes(toByteArray())
    }

    public fun toByteArray(): ByteArray {
        val writer = ByteWriter(headerSize)
        writer.writeByte(headerSize.toUByte())
        writer.writeByte(protocolVersionByte)
        writer.writeUShort(profileVersion)
        writer.writeUInt(dataSize)
        writer.writeBytes(dataType.encodeToByteArray())
        if (hasCrc) writer.writeUShort(headerCrc)
        return writer.toByteArray()
    }

    public companion object {
        public const val FIT_DATA_TYPE: String = ".FIT"

        /**
         * Reads a header at the reader's current position.
         *
         * Does not validate: a caller that wants to know whether this is really a
         * FIT file checks [isValid] afterwards. Callers that only want to probe
         * should copy the reader position first, since this advances it.
         */
        public fun read(reader: ByteReader): FileHeader {
            val headerSize = reader.readByte().toInt()
            val protocolVersionByte = reader.readByte()
            val profileVersion = reader.readUShort()
            val dataSize = reader.readUInt()
            val dataType = reader.readString(4)

            val header = FileHeader(headerSize, protocolVersionByte, profileVersion, dataSize, dataType)
            if (headerSize == Fit.HEADER_WITH_CRC_SIZE) {
                header.headerCrc = reader.readUShort()
            }
            return header
        }
    }
}
